/**
 * Image
 *
 * - lazy: 뷰포트에 들어올 때 로드하는 lazy loading
 * - sources: avif, webp 등 여러 포맷 지원으로 브라우저가 최적 포맷 선택
 */
import { useMemo, useState } from "react";
import useIntersectionObserver from "@base/model/hooks/useIntersectionObserver";
import { cn } from "@base/lib/utils";

export interface ImageSource {
  srcSet: string;
  type: string;
}

export interface ImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src"> {
  src: string;
  alt?: string;
  /** lazy=true일 때 뷰포트에 진입할 때까지 이미지 로드를 지연 */
  lazy?: boolean;
  /** avif, webp 등 여러 포맷을 제공하여 브라우저가 최적 포맷 선택 가능 */
  sources?: ImageSource[];
}

export function Image({ src, alt = "", lazy = false, sources, className, loading, onLoad, ...rest }: ImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const observerOptions = useMemo(() => ({ rootMargin: "50px" }), []);
  const { ref, isIntersecting } = useIntersectionObserver(observerOptions);

  const shouldLoad = !lazy || isIntersecting;
  const loadingStrategy = loading ?? (lazy ? "lazy" : "eager");

  const imageElement = (
    <img
      ref={lazy ? ref : undefined}
      src={shouldLoad ? src : undefined}
      className={cn(className, isLoaded && "loaded", lazy && "lazy-loading-image")}
      loading={shouldLoad ? loadingStrategy : undefined}
      decoding="async"
      {...rest}
      alt={alt}
      onLoad={(e) => {
        setIsLoaded(true);
        onLoad?.(e);
      }}
    />
  );

  if (sources && sources.length > 0 && shouldLoad) {
    return (
      <picture>
        {sources.map(({ srcSet, type }) => (
          <source key={type} srcSet={srcSet} type={type} />
        ))}
        {imageElement}
      </picture>
    );
  }

  return imageElement;
}
